import { cn } from "@/lib/utils";
import { useCurrentTenant } from "@/hooks/useTenant";
import { useTheme } from "@/hooks/useTheme";

interface BrandProps {
  collapsed?: boolean;
  className?: string;
  /** "sm" = sidebar (40 px), "lg" = auth screens (72 px). */
  size?: "sm" | "lg";
}

/**
 * Logo-OR-text — never both. If the broker has uploaded a logo image, we
 * render just that (since most brand marks already include the name).
 * Otherwise we render the name + subtitle text. Switching is automatic
 * based on tenant.logoUrl.
 */
export function Brand({ collapsed, className, size = "sm" }: BrandProps) {
  const { data } = useCurrentTenant();
  const { resolved } = useTheme();
  const tenant = data?.tenant;
  const isBroker = tenant && !tenant.isPlatform;
  const displayName = isBroker ? tenant.name : "AXProp";
  const subtitle = isBroker ? "Broker Console" : "CRM Console";

  // Pick the right logo for the active theme. Always fall back to the
  // default `tenants.logoUrl` so single-mark brokers still work on both
  // themes. `logoLightUrl` only fires in light mode; `logoDarkUrl` only
  // in dark mode.
  const branding = isBroker ? tenant.settings?.branding : undefined;
  const defaultLogo = isBroker ? tenant.logoUrl : null;
  const logoUrl = resolved === "light"
    ? (branding?.logoLightUrl ?? defaultLogo)
    : (branding?.logoDarkUrl ?? defaultLogo);

  const imgHeight = size === "lg" ? "h-16" : "h-10";
  const imgMax = size === "lg" ? "max-w-[240px]" : "max-w-[180px]";
  const nameSize = size === "lg" ? "text-2xl" : "text-base";
  const subSize = size === "lg" ? "text-xs" : "text-[10px]";

  // Logo branch — render the image as-is (preserve aspect ratio, no badge,
  // no duplicated brand name). Falls through to text on broken URL.
  if (logoUrl) {
    return (
      <div className={cn("flex items-center select-none", className)}>
        <img
          src={logoUrl}
          alt={`${displayName} logo`}
          className={cn(imgHeight, imgMax, "w-auto object-contain")}
          onError={(e) => {
            // If the logo fails to load, swap to the text branch by replacing
            // ourselves with a sibling node.
            const el = e.currentTarget as HTMLImageElement;
            el.style.display = "none";
            const fallback = el.nextElementSibling as HTMLElement | null;
            if (fallback) fallback.style.display = "flex";
          }}
        />
        <div className="hidden flex-col leading-tight">
          <span className={cn("font-semibold tracking-tight text-fg", nameSize)}>{displayName}</span>
          <span className={cn("uppercase tracking-widest text-fg-subtle", subSize)}>{subtitle}</span>
        </div>
      </div>
    );
  }

  // Text branch — no logo uploaded. Show name + subtitle.
  return (
    <div className={cn("flex flex-col leading-tight select-none", className)}>
      <span className={cn("font-semibold tracking-tight text-fg", nameSize)}>{displayName}</span>
      {!collapsed && (
        <span className={cn("uppercase tracking-widest text-fg-subtle", subSize)}>{subtitle}</span>
      )}
    </div>
  );
}
